import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from './SEOHead';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { 
  Briefcase, MapPin, Clock, IndianRupee, Building2, 
  Bookmark, BookmarkCheck, ArrowLeft, Calendar, CheckCircle, Upload, FileText, X
} from 'lucide-react';
import { toast } from 'sonner';
import Footer from './Footer';
import JsonLd from './JsonLd';

const API = process.env.REACT_APP_BACKEND_URL;

const JobDetails = () => {
  const { jobId } = useParams();
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const cvInputRef = useRef(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [coverNote, setCoverNote] = useState('');
  const [applying, setApplying] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [uploadingCV, setUploadingCV] = useState(false);
  const [localCVUrl, setLocalCVUrl] = useState(null); // For newly uploaded CV

  useEffect(() => {
    fetchJobDetails();
    if (user) checkIfSaved();
  }, [jobId]); // eslint-disable-line

  const fetchJobDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setJob(data.job);
      } else {
        toast.error('Job not found');
        navigate('/job-board');
      }
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/my/saved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setIsSaved(data.jobs.some(j => j.id === jobId));
      }
    } catch (error) {
      // Ignore
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${jobId}/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setIsSaved(data.saved);
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('CV file must be less than 5MB');
      return;
    }

    setUploadingCV(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/api/upload-cv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        // Backend returns 'url' not 'cv_url'
        setLocalCVUrl(data.url);
        // Update user context with new CV
        if (setUser && user) {
          setUser({
            ...user,
            profile: { ...user.profile, cv_url: data.url }
          });
        }
        toast.success('CV uploaded successfully!');
      } else {
        toast.error(data.detail || 'Failed to upload CV');
      }
    } catch (error) {
      console.error('CV upload error:', error);
      toast.error('Failed to upload CV');
    } finally {
      setUploadingCV(false);
    }
  };

  const hasCVAvailable = user?.profile?.cv_url || localCVUrl;

  const handleApply = async () => {
    if (!hasCVAvailable) {
      toast.error('Please upload your CV before applying');
      return;
    }

    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/jobs/${jobId}/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cover_note: coverNote })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Application submitted successfully!');
        setShowApplyModal(false);
        setJob(prev => ({ ...prev, has_applied: true, application_status: 'applied' }));
      } else {
        toast.error(data.detail || 'Failed to apply');
      }
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const formatSalary = (min, max, currency) => {
    if (!min && !max) return null;
    const format = (num) => {
      if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
      if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
      return num.toLocaleString();
    };
    if (min && max) return `${format(min)} - ${format(max)} ${currency}/year`;
    if (min) return `From ${format(min)} ${currency}/year`;
    return `Up to ${format(max)} ${currency}/year`;
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <p>Loading job details...</p>
      </div>
    );
  }

  if (!job) return null;

  // ───────── JSON-LD: JobPosting schema for Google rich results ─────────
  const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').trim();
  const jobPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: stripHtml(job.description) || job.title,
    datePosted: job.created_at,
    employmentType: (job.job_type || '').toUpperCase().replace('-', '_'),
    hiringOrganization: {
      '@type': 'Organization',
      name: job.company_name || 'Nerd Nation',
      ...(job.company_logo && !job.company_logo.startsWith('data:')
        ? { logo: `${API}${job.company_logo}` }
        : {}),
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.location || 'India',
        addressCountry: 'IN',
      },
    },
    ...(job.location_type === 'remote'
      ? { jobLocationType: 'TELECOMMUTE', applicantLocationRequirements: { '@type': 'Country', name: 'India' } }
      : {}),
    ...(job.salary_min || job.salary_max
      ? {
          baseSalary: {
            '@type': 'MonetaryAmount',
            currency: job.salary_currency || 'INR',
            value: {
              '@type': 'QuantitativeValue',
              ...(job.salary_min ? { minValue: job.salary_min } : {}),
              ...(job.salary_max ? { maxValue: job.salary_max } : {}),
              unitText: 'YEAR',
            },
          },
        }
      : {}),
    ...(job.tags && job.tags.length ? { skills: job.tags.join(', ') } : {}),
    ...(job.experience_min !== undefined
      ? { experienceRequirements: `${job.experience_min}-${job.experience_max || job.experience_min} years` }
      : {}),
    url: `https://nerdnation.world/jobs/${jobId}`,
    identifier: { '@type': 'PropertyValue', name: 'Nerd Nation Job ID', value: jobId },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead
        title={job.title ? `${job.title}${job.company_name ? ' at ' + job.company_name : ''}` : 'Job Details'}
        description={job.description?.slice(0, 160) || `Apply to ${job.title} on Nerd Nation Job Board`}
        path={`/jobs/${jobId}`}
        ogType="job"
      />
      <JsonLd data={jobPostingSchema} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/job-board')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Job Board
        </Button>

        <Card>
          <CardContent className="p-6 sm:p-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
              {job.company_logo ? (
                <img 
                  src={job.company_logo.startsWith('data:') ? job.company_logo : `${API}${job.company_logo}`} 
                  alt={job.company_name} 
                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0" loading="lazy" decoding="async"/>
              ) : (
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-8 w-8 text-orange-600" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
                    <p className="text-lg text-gray-600">{job.company_name}</p>
                  </div>
                  <button 
                    onClick={handleSave}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title={isSaved ? 'Remove from saved' : 'Save job'}
                  >
                    {isSaved ? (
                      <BookmarkCheck className="h-6 w-6 text-orange-500" />
                    ) : (
                      <Bookmark className="h-6 w-6 text-gray-400" />
                    )}
                  </button>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" /> {job.location}
                  </span>
                  <Badge variant="secondary" className="capitalize">{job.location_type}</Badge>
                  <Badge variant="secondary" className="capitalize">{job.job_type.replace('-', ' ')}</Badge>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" /> Posted {getTimeAgo(job.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Salary & Experience */}
            <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b">
              {formatSalary(job.salary_min, job.salary_max, job.salary_currency) && (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                  <IndianRupee className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-700">
                    {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-700">
                  {job.experience_min === 0 && job.experience_max === 0
                    ? 'Freshers welcome'
                    : `${job.experience_min} - ${job.experience_max} years experience`}
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">About the Role</h2>
              <div 
                className="rich-text-content text-gray-600"
                dangerouslySetInnerHTML={{ __html: job.description }}
              />
            </div>

            {/* Requirements */}
            {job.requirements && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h2>
                <div 
                  className="rich-text-content text-gray-600"
                  dangerouslySetInnerHTML={{ __html: job.requirements }}
                />
              </div>
            )}

            {/* Tags */}
            {job.tags && job.tags.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Skills & Tags</h2>
                <div className="flex flex-wrap gap-2">
                  {job.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-orange-50 text-orange-700">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Apply Button */}
            <div className="pt-6 border-t">
              {job.has_applied ? (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">You have applied to this job</span>
                  <Badge className="ml-2 capitalize">{job.application_status}</Badge>
                </div>
              ) : (
                <Button 
                  onClick={() => setShowApplyModal(true)}
                  className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-3 h-auto"
                >
                  <Briefcase className="h-5 w-5 mr-2" /> Easy Apply
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Apply Modal */}
        <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Apply to {job.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">You will apply with:</p>
                <p className="font-medium">{user?.full_name}</p>
                <p className="text-sm text-gray-500">{user?.email}</p>
                
                {/* CV Status */}
                {hasCVAvailable ? (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" /> 
                      <span>CV attached</span>
                    </p>
                    <a 
                      href={(localCVUrl || user?.profile?.cv_url)?.startsWith('data:') ? (localCVUrl || user?.profile?.cv_url) : `${API}${localCVUrl || user?.profile?.cv_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-green-600 hover:underline flex items-center gap-1 mt-1"
                    >
                      <FileText className="h-3 w-3" /> View CV
                    </a>
                    {/* Option to replace CV */}
                    <div className="mt-2">
                      <input
                        ref={cvInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleCVUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => cvInputRef.current?.click()}
                        disabled={uploadingCV}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        {uploadingCV ? 'Uploading...' : 'Replace CV'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-700 mb-2">
                      ⚠️ No CV found. Please upload your CV to apply.
                    </p>
                    <input
                      ref={cvInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleCVUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => cvInputRef.current?.click()}
                      disabled={uploadingCV}
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      {uploadingCV ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload CV (PDF or Word)
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1 text-center">Max 5MB</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Why are you the best candidate for this position? (Optional)
                </label>
                <Textarea
                  placeholder="Write 2-3 lines about why you're a great fit..."
                  value={coverNote}
                  onChange={(e) => setCoverNote(e.target.value)}
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-gray-400 mt-1">{coverNote.length}/500 characters</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplyModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleApply} 
                disabled={applying || !hasCVAvailable}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {applying ? 'Submitting...' : 'Submit Application'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default JobDetails;
